import { mediaUrl } from "./media-url";

export const POKER_CHIP_PICKUP_SOUNDS = [
  mediaUrl("/assets/audio/poker-chips/chip-pickup-01.mp3"),
  mediaUrl("/assets/audio/poker-chips/chip-pickup-02.mp3"),
  mediaUrl("/assets/audio/poker-chips/chip-pickup-03.mp3"),
  mediaUrl("/assets/audio/poker-chips/chip-pickup-04.mp3"),
  mediaUrl("/assets/audio/poker-chips/chip-pickup-05.mp3"),
  mediaUrl("/assets/audio/poker-chips/chip-pickup-06.mp3"),
] as const;

type AudioClip = { source: string; voices: HTMLAudioElement[]; nextVoice: number };

export type PokerChipAudioBank = { clips: AudioClip[]; lastClip: number };

type AudioContextConstructor = new (options?: AudioContextOptions) => AudioContext;
type PokerChipImpactEngine = { context: AudioContext; output: DynamicsCompressorNode; buffers: Map<string, AudioBuffer>; lastClip: number };

let impactEngine: PokerChipImpactEngine | undefined;
let impactPreloadJob: Promise<void> | undefined;

export function selectPokerChipPickupIndex(previous: number, random = Math.random) {
  const count = POKER_CHIP_PICKUP_SOUNDS.length;
  const available = previous >= 0 && previous < count ? count - 1 : count;
  const sample = Math.max(0, Math.min(0.999999999, random()));
  const selected = Math.floor(sample * available);
  return available === count || selected < previous ? selected : selected + 1;
}

export function createPokerChipAudioBank(voiceCount = 2): PokerChipAudioBank | undefined {
  if (typeof Audio === "undefined") return undefined;
  const voicesPerClip = Math.max(1, Math.floor(voiceCount));
  return {
    lastClip: -1,
    clips: POKER_CHIP_PICKUP_SOUNDS.map((source) => ({
      source,
      nextVoice: 0,
      voices: Array.from({ length: voicesPerClip }, () => {
        const audio = new Audio(source);
        audio.preload = "auto";
        audio.volume = 0.7;
        audio.load();
        return audio;
      }),
    })),
  };
}

export function playRandomPokerChipPickup(bank: PokerChipAudioBank | undefined, random = Math.random) {
  if (!bank?.clips.length) return undefined;
  const clipIndex = selectPokerChipPickupIndex(bank.lastClip, random);
  const clip = bank.clips[clipIndex];
  bank.lastClip = clipIndex;
  const idleVoice = clip.voices.findIndex((voice) => voice.paused || voice.ended);
  const voiceIndex = idleVoice >= 0 ? idleVoice : clip.nextVoice;
  clip.nextVoice = (voiceIndex + 1) % clip.voices.length;
  const voice = clip.voices[voiceIndex];
  try {
    voice.currentTime = 0;
    void voice.play().catch(() => undefined);
  } catch {
    // A failed sound must never interrupt the chip interaction.
  }
  return clip.source;
}

export function disposePokerChipAudioBank(bank: PokerChipAudioBank | undefined) {
  bank?.clips.forEach((clip) => clip.voices.forEach((voice) => {
    voice.pause();
    voice.removeAttribute("src");
  }));
}

function createPokerChipImpactEngine() {
  if (impactEngine) return impactEngine;
  const constructor = (typeof AudioContext === "undefined" ? (globalThis as typeof globalThis & { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext : AudioContext) as AudioContextConstructor | undefined;
  if (!constructor) return undefined;
  const context = new constructor({ latencyHint: "interactive" }); const output = context.createDynamicsCompressor();
  output.threshold.value = -20; output.knee.value = 18; output.ratio.value = 8; output.attack.value = .002; output.release.value = .16; output.connect(context.destination);
  impactEngine = { context, output, buffers: new Map(), lastClip: -1 }; return impactEngine;
}

/** Fetches and decodes all six single-chip clips before the table is shown. */
export function preloadPokerChipImpactAudio() {
  if (impactPreloadJob) return impactPreloadJob;
  const engine = createPokerChipImpactEngine(); if (!engine) return Promise.resolve();
  impactPreloadJob = Promise.all(POKER_CHIP_PICKUP_SOUNDS.map(async (source) => {
    const response = await fetch(source, { cache: "force-cache" }); if (!response.ok) throw new Error(`Could not preload ${source}`);
    const buffer = await engine.context.decodeAudioData(await response.arrayBuffer()); engine.buffers.set(source, buffer);
  })).then(() => undefined);
  return impactPreloadJob;
}

/** Resumes the low-latency Web Audio clock from the explicit Arm gesture. */
export async function unlockPokerChipImpactAudio() {
  const engine = createPokerChipImpactEngine(); if (engine?.context.state === "suspended") await engine.context.resume().catch(() => undefined);
}

export type PokerChipImpactPlayback = { source: string; played: boolean };

/** Unlimited buffer voices preserve one audible contact for every physical chip. */
export function playPokerChipImpactSound(delayMs = 0, random = Math.random): PokerChipImpactPlayback {
  const engine = createPokerChipImpactEngine(); const clipIndex = selectPokerChipPickupIndex(engine?.lastClip ?? -1, random); const source = POKER_CHIP_PICKUP_SOUNDS[clipIndex];
  if (!engine) return { source, played: false }; engine.lastClip = clipIndex; const buffer = engine.buffers.get(source); if (!buffer || engine.context.state === "closed") return { source, played: false };
  const voice = engine.context.createBufferSource(); const gain = engine.context.createGain(); voice.buffer = buffer; voice.playbackRate.value = .96 + random() * .08; gain.gain.value = .3; voice.connect(gain); gain.connect(engine.output); voice.start(engine.context.currentTime + Math.max(0, delayMs) / 1_000);
  return { source, played: true };
}
